import type { 
  MapProvider, 
  MapProviderType, 
} from 'src/types/map-provider';
import { 
  MapProviderError,
  MapProviderLoadError,
  MapProviderNotSupportedError
} from 'src/types/map-provider';
import { isUserInChina } from './country-detection';


const loadedProviders = new Map<MapProviderType, MapProvider>();
const loadingPromises = new Map<MapProviderType, Promise<MapProvider>>();

/**
 * Create a map provider instance with dynamic loading
 * Uses caching to avoid loading the same provider multiple times
 */
export async function createMapProvider(type: MapProviderType, apiKey?: string): Promise<MapProvider> {
  const cached = loadedProviders.get(type);
  if (cached) {
    return cached;
  }

  const loading = loadingPromises.get(type);
  if (loading) {
    return loading;
  }

  if (!isProviderSupported(type)) {
    throw new MapProviderNotSupportedError(type);
  }

  const loadingPromise = loadMapProvider(type, apiKey);
  loadingPromises.set(type, loadingPromise);

  try {
    const provider = await loadingPromise;
    
    loadedProviders.set(type, provider);
    loadingPromises.delete(type);
    
    return provider;
  } catch (error) {
    loadingPromises.delete(type);
    throw error;
  }
}

/**
 * Create provider with automatic selection based on user location
 */
export async function createMapProviderAuto(apiKeys?: Partial<Record<MapProviderType, string>>): Promise<{
  provider: MapProvider;
  type: MapProviderType;
}> {
  try {
    const isInChina = await isUserInChina({ timeout: 3000 });
    const type: MapProviderType = isInChina ? 'baidu' : 'google';
    const apiKey = apiKeys?.[type];
    
    const provider = await createMapProvider(type, apiKey);
    return { provider, type };
  } catch (error) {
    
    try {
      const type: MapProviderType = 'google';
      const provider = await createMapProvider(type, apiKeys?.google);
      return { provider, type };
    } catch (googleError) {
      const type: MapProviderType = 'openstreetmap';
      const provider = await createMapProvider(type);
      return { provider, type };
    }
  }
}

/**
 * Check if a provider type is supported
 * 
 */
// TODO: this is hardcoded for now, but we should make it dynamic
export function isProviderSupported(type: MapProviderType): boolean {
  return ['google', 'baidu', 'openstreetmap'].includes(type);
}

/**
 * Dynamically load a provider module
 *
 * Uses explicit switch statement to make imports statically analyzable by webpack.
 * This avoids the "Critical dependency" warning while maintaining code splitting benefits.
 *
 * @param type - The type of provider to load
 * @param apiKey - The API key to use for the provider (optional)
 *
 * @returns The loaded provider
 */
async function loadMapProvider(type: MapProviderType, apiKey?: string): Promise<MapProvider> {
  try {
    let ProviderClass;

    // Use explicit imports so webpack can statically analyze and properly code-split
    switch (type) {
      case 'google':
        ProviderClass = (await import('../providers/google-maps.provider')).default;
        break;
      case 'baidu':
        ProviderClass = (await import('../providers/baidu-maps.provider')).default;
        break;
      case 'openstreetmap':
        ProviderClass = (await import('../providers/openstreetmap.provider')).default;
        break;
      default:
        throw new MapProviderNotSupportedError(type);
    }

    const provider = new ProviderClass();

    if (apiKey && 'loadScript' in provider && typeof provider.loadScript === 'function') {
      await provider.loadScript(apiKey);
    }

    return provider;
  } catch (error) {
    if (error instanceof MapProviderError) {
      throw error;
    }

    throw new MapProviderLoadError(type, error instanceof Error ? error : new Error(String(error)));
  }
}
